"use client";

import React from "react";
import { cn } from "utils";
import { ospThemes } from "@ospweb/sdk-ui";
import { EdgenModalCreateAble } from "../../EdgenModal";
import type { CommonCenterConfirmModalProps } from "./types";

/**
 * CommonCenterConfirmModal - 居中确认弹窗组件
 * 用于显示简单的确认信息，包含标题、描述和单个操作按钮
 */
export const CommonCenterConfirmModal: React.FC<
  CommonCenterConfirmModalProps
> = ({ title, description, buttonText, onClick }) => {
  return (
    <div className="p-6 bg-background_solid_secondary flex flex-col items-center gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <h2
          aria-label="Common Center Confirm Modal h2"
          data-openreplay-label="Common Center Confirm Modal h2 Semantic"
          id="sem-common-center-confirm-modal-h2"
          className="headline_regular_s text-content_primary"
        >
          {title}
        </h2>
        <p className="body_m text-content_secondary whitespace-pre-wrap">
          {description}
        </p>
      </div>
      <button
        aria-label="Common Center Confirm Modal"
        data-openreplay-label={`Common Center Confirm Modal Button | ${buttonText}`}
        id="btn-common-center-confirm-modal"
        className={cn(
          "button primary_button md_button w-full",
          "bg-background_white text-content_inverse_primary",
          "button_m group h-11",
          "disabled:opacity-50 disabled:cursor-not-allowed",
        )}
        onClick={onClick}
      >
        {buttonText}
      </button>
    </div>
  );
};

/**
 * Callable version of CommonCenterConfirmModal
 * 可通过函数调用的方式使用
 */
export const CommonCenterConfirmModalCallable = async (
  props: Omit<CommonCenterConfirmModalProps, "onClick"> & {
    onClick?: () => void;
    modalProps?: any;
  },
): Promise<boolean> => {
  const { modalProps, onClick, ...otherProps } = props;
  return new Promise<boolean>((resolve) => {
    EdgenModalCreateAble.call({
      children: (
        <CommonCenterConfirmModal
          data-openreplay-label="CommonCenterConfirmModal · CommonCenterConfirmModal · CommonCenterConfirmModal"
          {...otherProps}
          onClick={() => {
            EdgenModalCreateAble.end(true);
            onClick?.();
            resolve(true);
          }}
        />
      ),
      onCancel: () => {
        EdgenModalCreateAble.end(false);
        resolve(false);
      },
      containerClassName: "!md:w-[400px] !md:rounded-2xl",
      styles: {
        content: {
          backgroundColor: ospThemes.colors.background_solid_secondary,
          padding: 0,
          borderRadius: "16px",
        },
      },
      centered: true,
      showClose: false,
      destroyOnClose: true,
      ...modalProps,
    });
  });
};
